// A restaurant's page across 640 px (UC-31, #1020). The list or cards switch
// changes place there, by CSS alone, and the pinned bar and the back row
// change height. By the time the media query says so the layout has already
// changed, so the item under the bar is scrolled back to the place noted on
// the last frame before it: the place is noted again on every scroll, resize,
// tap and keystroke, which are what move it, a filter's change included.
// The browser runs a frame's media query changes before its animation frame
// callbacks, so the crossing always reads the place from before the change.
//
// Focus on the copy that hides moves to the same option in the copy that
// shows. The browser may already have let go of the hidden copy's focus by
// then, so the option focused is kept from the focus events: a blur while
// that copy still shows is the diner's own, and forgets it.

import { drawn, note, restore, type Page, type Place } from './anchor';

/** What the crossing listens to: the window, the document, the media query. */
export interface Target {
	addEventListener(type: string, listener: (event: Event) => void, options?: object): void;
	removeEventListener(type: string, listener: (event: Event) => void): void;
}

export interface Deps {
	page: Page | null;
	/** The `(min-width: 640px)` query, whose `change` is the crossing. */
	wide: Target;
	win: Target;
	doc: Target & {
		activeElement: Element | null;
		body: Element | null;
		querySelectorAll(selector: string): Iterable<Element>;
	};
	frame: (callback: () => void) => number;
	cancel: (id: number) => void;
}

const COPY = '[data-view-place]';

const options = (copy: Element) => [...copy.querySelectorAll<HTMLElement>('button')];

/** Starts listening; returns the stop. */
export function crossing(d: Deps): () => void {
	let place: Place | null = null;
	let frame = 0;
	let held: { copy: Element; option: number } | null = null;

	const mark = () => {
		const page = d.page;
		if (frame || !page) return;
		frame = d.frame(() => {
			frame = 0;
			place = note(page);
		});
	};

	const focusIn = (event: Event) => {
		const target = event.target as HTMLElement | null;
		const copy = target?.closest?.(COPY) ?? null;
		held = copy && target ? { copy, option: options(copy).indexOf(target) } : null;
	};

	const focusOut = (event: Event) => {
		if (held && !(event as FocusEvent).relatedTarget && drawn(held.copy)) held = null;
	};

	const cross = () => {
		if (d.page) restore(d.page, place);
		const from = held;
		if (!from || from.option < 0 || drawn(from.copy)) return;
		const active = d.doc.activeElement;
		if (active && active !== d.doc.body && !from.copy.contains(active)) return;
		const to = [...d.doc.querySelectorAll(COPY)].find((copy) => copy !== from.copy && drawn(copy));
		if (to) options(to)[from.option]?.focus({ preventScroll: true });
	};

	const moves = ['scroll', 'resize'] as const;
	const acts = ['input', 'click'] as const;
	mark();
	for (const type of moves) d.win.addEventListener(type, mark, { passive: true });
	for (const type of acts) d.doc.addEventListener(type, mark, { passive: true });
	d.doc.addEventListener('focusin', focusIn);
	d.doc.addEventListener('focusout', focusOut);
	d.wide.addEventListener('change', cross);
	return () => {
		d.cancel(frame);
		for (const type of moves) d.win.removeEventListener(type, mark);
		for (const type of acts) d.doc.removeEventListener(type, mark);
		d.doc.removeEventListener('focusin', focusIn);
		d.doc.removeEventListener('focusout', focusOut);
		d.wide.removeEventListener('change', cross);
	};
}
